<template>
  <slide-out
    dock="right"
    :size="sideOutCurrentSize"
    ref="roleSlideout"
    @resize="OnResize"
    :min-size="sideOutMinSize"
    :allow-resize="nested ? false : true"
    :append-to="nested ? '' : '#app'"
    :visible.sync="slideouts.active"
    v-on:close="onSlideoutClosing">
    <template v-slot:header>
      <h3 v-if="isFetching" class="font-weight-bold pa-1" small>
        <v-progress-circular :size="16" width="2" indeterminate class="mr-1"></v-progress-circular>
        Fetching Role ...
      </h3>
      <h3 v-else-if="selected.id" class="font-weight-bold px-1" small>
        <v-icon left>mdi-pencil-box-outline</v-icon> Update: '{{ selected.name }}'
      </h3>
      <h3 v-else class="font-weight-bold px-1" small>
        <v-icon left>mdi-plus-box-outline</v-icon> Add Role
      </h3>
      <v-btn @click="closeUpdateSlideout()" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div>
      <v-form ref="editRoleForm" v-model="valid">
        <v-container class="pa-6">
          <v-row class="my-0" dense>
            <v-col cols="12" sm="12">
              <v-text-field
                :rules="[formRules.required, formRules.length(2)]"
                label="Role Name"
                id="roleName"
                ref="roleName"
                placeholder="Role Name e.g. Admin"
                v-model="selected.name"
                :readonly="selected.isSystem"
                hide-details
                class="mb-0"
                outlined
                dense>
              </v-text-field>
            </v-col>
            <!-- <v-col cols="12" sm="12" v-if="mode === 'management' && company">
              <div class="d-flex align-center" style="gap: 8px">
                <v-text-field
                  label="Max Users"
                  id="maxUsers"
                  type="number"
                  placeholder="Maximum number of users"
                  v-model.number="selected.maxUsers"
                  :disabled="unlimitedMaxUsers"
                  hide-details
                  class="mb-0"
                  outlined
                  dense>
                </v-text-field>
                <v-checkbox
                  v-model="unlimitedMaxUsers"
                  label="Unlimited"
                  dense
                  hide-details
                  class="mb-0 ml-2"
                  @change="onUnlimitedMaxUsersChange"
                ></v-checkbox>
              </div>
            </v-col> -->
            <v-col cols="12">
              <v-divider class="my-2"></v-divider>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="search"
                hide-details
                clearable
                placeholder="Search in permissions"
                outlined
                dense
                prepend-inner-icon="mdi-magnify">
              </v-text-field>
            </v-col>
            <v-col cols="12" class="py-1">
              <div class="d-flex justify-end">
                <v-btn
                  small
                  text
                  color="info"
                  @click="expandAll"
                  :disabled="permissionsLoading || !treeReady"
                >
                  <v-icon left small>mdi-expand-all</v-icon>
                  Expand All
                </v-btn>
                <v-btn
                  small
                  text
                  color="info"
                  @click="collapseAll"
                  :disabled="permissionsLoading || !treeReady"
                >
                  <v-icon left small>mdi-collapse-all</v-icon>
                  Collapse All
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12">
              <div style="border-radius: 0.5rem" class="pa-2 tree-wrapper">
                <div v-if="permissionsLoading || !treeReady" class="d-flex justify-center align-center" style="min-height: 200px;">
                  <v-progress-circular
                    color="primary"
                    :width="2"
                    :rotate="-90"
                    :size="24"
                    indeterminate></v-progress-circular>
                </div>
                <template v-else>
                  <v-treeview
                    v-if="permissions.length"
                    v-model="selected.permissionIds"
                    :items="permissions"
                    open-on-click
                    selectable
                    :return-object="false"
                    :open="openNodes"
                    selected-color="primary"
                    :search="search"
                    hoverable
                    transition
                    selection-type="leaf"
                    @update:open="openNodes = $event"
                    @input="checkForChanges">
                    <template #prepend="{ item, open, selected, leaf, indeterminate }">
                      <!-- Leaf node: key icon -->
                      <v-icon
                        style="font-size: 1.3rem"
                        v-if="leaf"
                        :class="
                          selected
                            ? 'fad primary--text'
                            : indeterminate
                            ? 'fad primary--text'
                            : 'fad grey--text'
                        ">
                        mdi-key
                      </v-icon>
                      <!-- Level 1 grouping node -->
                      <v-icon
                        v-else-if="isGroupingNode(item)"
                        style="font-size: 1.3rem"
                        :class="
                          selected
                            ? 'fad primary--text'
                            : indeterminate
                            ? 'fad primary--text'
                            : 'fad grey--text'
                        ">
                        {{ open ? 'mdi-package-variant-closed' : 'mdi-package-variant' }}
                      </v-icon>
                      <!-- Level 2+ folder nodes -->
                      <v-icon
                        v-else
                        :class="
                          selected
                            ? 'fad primary--text'
                            : indeterminate
                            ? 'fad primary--text'
                            : 'fad grey--text'
                        "
                        style="font-size: 1.3rem">
                        {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                      </v-icon>
                    </template>
                    <template #label="{ item, selected, indeterminate }">
                      <span
                        :class="[
                          selected
                            ? 'primary--text'
                            : indeterminate
                            ? 'primary--text'
                            : 'grey--text',
                          isGroupingNode(item) ? 'font-weight-bold' : ''
                        ]"
                        style="font-size: 1rem">
                        {{ isGroupingNode(item) ? item.name : $splitCamelCase(item.name) }}
                      </span>
                    </template>
                  </v-treeview>
                  <div v-else class="pa-16 d-flex align-center justify-center flex-column">
                    <v-img max-height="350px" contain src="/img/notFound.svg"></v-img>
                    <div class="pt-4 title">No Permissions Found</div>
                  </div>
                </template>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </div>
    <template v-slot:footer>
      <v-card-actions>
        <v-btn text color="error" @click="closeUpdateSlideout()">
          <v-icon left>mdi-chevron-left</v-icon> Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
        
          color="primary"
          :disabled="!valid || slideouts.isLoading || !slideouts.hasChanges || !canSaveChanges || selected.isSystem"
          @click="updateConfirmed()"
          :loading="slideouts.isLoading">
          <v-icon left>{{ selected.id ? 'mdi-check' : 'mdi-plus' }}</v-icon>
          {{ selected.id ? 'Save Changes' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </template>
  </slide-out>
</template>

<script>
  import formRules from '@Share/config/form-rules';
  import { $handleError, $has, $isEqual, $splitCamelCase } from '@/utils';
  import * as appPermissions  from '@/features/Share/config/permissions';
  import managementRolesService from '@Company/service/management-roles.service';
  import { log, warn, error } from '@/utils/debugLogger';
  export default {
    name: 'edit-role',
    data() {
      return {
        appPermissions,
        valid: false,
        formRules,
        isFetching: false,
        selected: {},
        modal: false,
        selectedCemented: {},
        search: '',
        treeReady: false,
        openNodes: [],
        mode: 'standard', // 'standard' or 'management'
        company: null, // For management mode
        slideouts: {
          tab: 0,
          active: false,
          isLoading: false,
          isClosing: false,
          hasChanges: false,
        },
      };
    },
    props: {
      nested: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      permissions() {
        return this.$store.getters['PERMISSION/GET_PERMISSION'];
      },
      canSaveChanges(){
          if(this.selected.id)
          return $has( this.appPermissions.default.Roles.Update)
        else{
          return $has( this.appPermissions.default.Roles.Create)
        }
      },
     
      permissionsLoading() {
        return this.$store.getters['PERMISSION/GET_LOADING'];
      },
      sideOutMinSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_MINSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_MINSIZE', val);
        },
      },
      sideOutCurrentSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_CURRENTSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_CURRENTSIZE', val);
        },
      },
    },
    methods: {
      $has,
      OnResize() {
        let roleSlideout = this.$refs.roleSlideout;
        this.sideOutCurrentSize = roleSlideout.resizeValue;
      },
      $splitCamelCase,
      isGroupingNode(item) {
        return typeof item.id === 'string' && item.id.startsWith('grouping::');
      },
      expandAll() {
        const ids = [];
        const collect = (nodes) => {
          if (!nodes) return;
          nodes.forEach(node => {
            if (node.children && node.children.length) {
              ids.push(node.id);
              collect(node.children);
            }
          });
        };
        collect(this.permissions);
        this.openNodes = ids;
      },
      collapseAll() {
        this.openNodes = [];
      },
      onSlideoutClosing() {
        this.$emit('close');
        this.closeUpdateSlideout();
      },
      updateConfirmed() {
        this.slideouts.isLoading = true;
        let toSend = structuredClone(this.selected);
        
        if (this.mode === 'management' && this.company) {
          // Use management-roles service for company-specific roles
          const companyId = this.company.id;
          let promise;
          
          if (toSend.id) {
            promise = managementRolesService.update(companyId, toSend);
          } else {
            promise = managementRolesService.add(companyId, toSend);
          }
          
          promise
            .then(() => {
              const message = toSend.id ? 'Role updated successfully!' : 'Role added successfully!';
              this.closeUpdateSlideout();
              this.$dialog.notify.success(message, {
                position: 'top-right',
                timeout: 3000,
              });
              // Emit event to refresh the role list
              this.$emit('role-updated');
            })
            .catch((err) => {
              error('Error updating role:', err);
              $handleError(err);
            })
            .finally(() => {
              this.slideouts.isLoading = false;
            });
        } else {
          // Use standard store dispatch for global roles
          this.$store
            .dispatch('ROLE/UPDATE', toSend)
            .then(() => {
              const message = toSend.id ? 'Role updated successfully!' : 'Role added successfully!';
              this.closeUpdateSlideout();
              this.$dialog.notify.success(message, {
                position: 'top-right',
                timeout: 3000,
              });
            })
            .catch((err) => {
              error('Error updating role:', err);
              $handleError(err);
            })
            .finally(() => {
              this.slideouts.isLoading = false;
            });
        }
      },
      closeUpdateSlideout() {
        this.selected = {};
        this.cementRole();
        this.slideouts.active = false;
        this.$refs.editRoleForm.resetValidation();
      },
      open(id, mode = 'standard', company = null) {
        this.treeReady = false;
        this.mode = mode || 'standard';
        this.company = company;

        // Get permissions based on mode
        if (this.mode === 'management' && this.company) {
          // Use management-roles service for company-specific permissions
          this.$store.commit('PERMISSION/SET_LOADING', true);
          managementRolesService.getPermissions(this.company.id)
            .then(response => {
              // Process permissions data to match the expected structure
              const permissionsData = response.data || [];
              // Store permissions in the same format as the standard permissions
              this.$store.commit('PERMISSION/SET_ENTRIES', permissionsData);
              this.$store.commit('PERMISSION/SET_LOADING', false);
            })
            .catch(err => {
              error('Error fetching management permissions:', err);
              this.$store.commit('PERMISSION/SET_LOADING', false);
              $handleError(err);
            });
        } else {
          // Use standard store dispatch for global permissions
          this.$store.dispatch('PERMISSION/GET');
        }
        
        if (id != null) {
          this.roleId = id;
          this.fetchRole();
          setTimeout(() => {
            this.slideouts.tab = 0;
          }, 250);
        } else {
          // Reset selected for new role
          this.selected = {};
          this.cementRole();
        }
        
        this.slideouts.active = true;
      },
      fetchRole() {
        this.isFetching = true;
        
        if (this.mode === 'management' && this.company) {
          // Use management-roles service for company-specific roles
          const companyId = this.company.id;
          
          managementRolesService.getById(companyId, this.roleId)
            .then((resp) => {
              this.isFetching = false;
              this.slideouts.isLoading = false;
              this.selected = structuredClone(resp.data);
              this.selected.permissionIds = this.selected.permissions.map((elm) => elm.id);
              delete this.selected.permissions;
              this.cementRole();
            })
            .catch((err) => {
              this.isFetching = false;
              this.closeUpdateSlideout();
              $handleError(err);
            });
        } else {
          // Use standard store dispatch for global roles
          this.$store
            .dispatch('ROLE/GET_BY_ID', this.roleId)
            .then((resp) => {
              this.isFetching = false;
              this.slideouts.isLoading = false;
              this.selected = structuredClone(resp.data);
              this.selected.permissionIds = this.selected.permissions.map((elm) => elm.id);
              delete this.selected.permissions;
              this.cementRole();
            })
            .catch((err) => {
              this.isFetching = false;
              this.closeUpdateSlideout();
              $handleError(err);
            });
        }
      },
      cementRole() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.slideouts.active) return;

        this.slideouts.hasChanges = !$isEqual(this.selected, this.selectedCemented);
      },
    },
    watch: {
      permissionsLoading(newVal) {
        if (!newVal && this.permissions.length) {
          // Permissions finished loading — defer treeReady by a tick
          // so Vue renders the loader first, then swaps to the tree
          this.$nextTick(() => {
            this.treeReady = true;
          });
        }
      },
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
      nested: {
        handler() {
          this.nested ? (this.sideOutCurrentSize = '80vw') : (this.sideOutCurrentSize = '35vw');
          this.nested ? (this.sideOutMinSize = '80vw') : (this.sideOutMinSize = '35vw');
        },
        deep: true,
      },
    },
  };
</script>
